<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn icon @click="goBack">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div class="ml-3">
                <h3 class="font-weight-bold">{{ ticket.title || 'Raise a ticket' }}</h3>
                <div class="text-caption" v-if="ticket.status">
                  <v-chip
                    :color="getStatusColor(ticket.status)"
                    text-color="white"
                    x-small
                  >
                    {{ formatStatus(ticket.status) }}
                  </v-chip>
                  <span class="ml-2 grey--text">Today {{ formatTime(ticket.created_at) }}</span>
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-chip
                v-if="isConnected"
                color="success"
                small
                dark
                class="mr-2"
              >
                <v-icon left small>mdi-wifi</v-icon>
                Live
              </v-chip>
              <v-btn
                v-if="isAdmin && ticket.status !== 'resolved'"
                color="success"
                @click="resolveTicket"
                :loading="resolving"
              >
                Mark as Resolved
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl" style="min-height: 600px; display: flex; flex-direction: column;">
            <v-card-text class="flex-grow-1 messages-container" ref="messagesContainer">
              <v-progress-linear
                v-if="loadingMessages"
                indeterminate
                color="primary"
              ></v-progress-linear>
              <div v-else>
                <div
                  v-for="message in messages"
                  :key="message.id"
                  class="message-wrapper mb-4"
                  :class="{ 'my-message': message.user_id === currentUserId }"
                >
                  <div class="d-flex" :class="{ 'justify-end': message.user_id === currentUserId }">
                    <v-avatar
                      v-if="message.user_id !== currentUserId"
                      :color="message.is_admin ? 'orange' : 'primary'"
                      size="32"
                      class="mr-2"
                    >
                      <span class="white--text text-caption">
                        {{ message.user && message.user.first_name ? message.user.first_name.charAt(0) : 'A' }}
                      </span>
                    </v-avatar>
                    <div class="message-bubble" :class="{ 'message-sent': message.user_id === currentUserId, 'message-received': message.user_id !== currentUserId }">
                      <div v-if="message.user_id !== currentUserId" class="message-header mb-1">
                        <strong>{{ getUserName(message.user) }}</strong>
                        <v-chip
                          v-if="message.is_admin"
                          color="orange"
                          text-color="white"
                          x-small
                          class="ml-2"
                        >
                          Support
                        </v-chip>
                      </div>
                      <div class="message-text">{{ message.message }}</div>
                      <div class="message-time">{{ formatTime(message.created_at) }}</div>
                    </div>
                    <v-avatar
                      v-if="message.user_id === currentUserId"
                      color="primary"
                      size="32"
                      class="ml-2"
                    >
                      <span class="white--text text-caption">
                        {{ currentUser && currentUser.first_name ? currentUser.first_name.charAt(0) : 'Y' }}
                      </span>
                    </v-avatar>
                  </div>
                </div>
                <div v-if="messages.length === 0" class="text-center grey--text py-5">
                  <v-icon size="64" color="grey">mdi-message-outline</v-icon>
                  <p class="mt-3">No messages yet. Start the conversation!</p>
                </div>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions v-if="!isResolved" class="pa-4">
              <v-text-field
                v-model="newMessage"
                label="Message..."
                outlined
                dense
                rounded
                hide-details
                @keydown.enter="sendMessage"
                :disabled="sending"
                class="flex-grow-1"
              ></v-text-field>
              <v-btn
                icon
                color="primary"
                class="ml-2"
                @click="sendMessage"
                :loading="sending"
                :disabled="!newMessage.trim()"
                large
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </v-card-actions>
            <v-card-actions v-else class="pa-4">
              <v-alert type="info" dense outlined class="mb-0 flex-grow-1">
                This ticket is resolved. No more messages can be sent.
              </v-alert>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar for notifications -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TicketChat',
  data() {
    return {
      ticket: {
        title: '',
        status: '',
        priority: '',
        created_at: ''
      },
      messages: [],
      newMessage: '',
      loadingMessages: false,
      sending: false,
      resolving: false,
      isConnected: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    ticketId() {
      return this.$route.params.id;
    },
    currentUserId() {
      return this.$store.state.auth?.user?.id;
    },
    currentUser() {
      return this.$store.state.auth?.user;
    },
    isAdmin() {
      const user = this.$store.state.auth?.user;
      if (!user || !user.roles || !Array.isArray(user.roles)) return false;
      return user.roles.some(role => role.id === 1 || role.id === 2);
    },
    isResolved() {
      return this.ticket.status === 'resolved';
    }
  },
  mounted() {
    this.loadTicket();
    this.loadMessages();
    this.checkConnection();
    this.subscribeToChannel();

    // Monitor WebSocket connection status
    if (this.$echo && this.$echo.connector && this.$echo.connector.pusher) {
      this.$echo.connector.pusher.connection.bind('connected', () => {
        this.isConnected = true;
        console.log('✅ WebSocket connected');
      });

      this.$echo.connector.pusher.connection.bind('disconnected', () => {
        this.isConnected = false;
        console.log('❌ WebSocket disconnected');
      });
    }
  },
  beforeDestroy() {
    this.unsubscribeFromChannel();
  },
  methods: {
    async loadTicket() {
      try {
        const response = await axios.get(`/tickets/${this.ticketId}`);
        if (response.data.success && response.data.ticket) {
          this.ticket = response.data.ticket;
        } else if (response.data.data) {
          this.ticket = response.data.data;
        }
      } catch (error) {
        console.error('Error loading ticket:', error);
        this.showSnackbar('Failed to load ticket', 'error');
      }
    },
    async loadMessages() {
      this.loadingMessages = true;
      try {
        const response = await axios.get('/ticket-messages', {
          params: { ticket_id: this.ticketId }
        });

        if (response.data.success && response.data.messages) {
          this.messages = response.data.messages;
        } else if (response.data.data) {
          this.messages = response.data.data;
        }

        // Mark messages as read
        await this.markMessagesAsRead();

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Error loading messages:', error);
        this.showSnackbar('Failed to load messages', 'error');
      } finally {
        this.loadingMessages = false;
      }
    },
    async markMessagesAsRead() {
      try {
        await axios.post('/ticket-messages/update-status', {
          ticket_id: this.ticketId
        });
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      this.sending = true;
      try {
        const response = await axios.post('/ticket-messages', {
          ticket_id: this.ticketId,
          message: this.newMessage
        });

        // Add message if not already added by WebSocket
        if (response.data.message || response.data.data) {
          const newMsg = response.data.message || response.data.data;
          const exists = this.messages.find(m => m.id === newMsg.id);
          if (!exists) {
            this.messages.push(newMsg);
          }
        }

        // Update ticket status if changed
        if (response.data.ticket) {
          this.ticket.status = response.data.ticket.status;
        }

        this.newMessage = '';
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Error sending message:', error);
        this.showSnackbar(error.response?.data?.message || 'Failed to send message', 'error');
      } finally {
        this.sending = false;
      }
    },
    async resolveTicket() {
      this.resolving = true;
      try {
        await axios.put(`/tickets/${this.ticketId}`, {
          status: 'resolved'
        });
        this.loadTicket();
        this.showSnackbar('Ticket resolved successfully', 'success');
      } catch (error) {
        console.error('Error resolving ticket:', error);
        this.showSnackbar('Failed to resolve ticket', 'error');
      } finally {
        this.resolving = false;
      }
    },
    checkConnection() {
      if (this.$echo && this.$echo.connector && this.$echo.connector.pusher) {
        const state = this.$echo.connector.pusher.connection.state;
        this.isConnected = state === 'connected';
        console.log(`🔍 WebSocket connection status: ${state}`);
      }
    },
    subscribeToChannel() {
      if (!this.$echo) {
        console.error('❌ Echo instance not available');
        return;
      }

      const channelName = `ticket.${this.ticketId}`;
      console.log(`📡 Attempting to subscribe to private channel: ${channelName}`);
      console.log('🔍 Current user ID:', this.currentUserId);
      console.log('🔍 Ticket ID:', this.ticketId);

      const channel = this.$echo.private(channelName);

      // Log all events on this channel for debugging
      channel.listenForWhisper('test', (e) => {
        console.log('👂 Whisper test:', e);
      });

      // Listen for the message.sent event
      channel.listen('.message.sent', (data) => {
        // console.log('🎉 New message received via WebSocket:', data);
        // console.log('📩 Message data:', JSON.stringify(data, null, 2));

        const newMessage = data.message;

        // Check if message already exists
        const exists = this.messages.find(m => m.id === newMessage.id);
        if (!exists) {
          // console.log('➕ Adding new message to list');
          this.messages.push(newMessage);
          this.$nextTick(() => {
            this.scrollToBottom();
          });

          // Show notification if message is from another user
          if (newMessage.user_id !== this.currentUserId) {
            const userName = newMessage.user ? `${newMessage.user.first_name}` : 'Support';
            this.showSnackbar(`New message from ${userName}`, 'info');
          }
        } else {
          console.log('⚠️ Message already exists, skipping');
        }
      });

      console.log('✅ Subscription setup complete for channel:', channelName);

      // Check WebSocket connection state
      if (this.$echo.connector && this.$echo.connector.pusher) {
        const state = this.$echo.connector.pusher.connection.state;
        console.log('🔗 WebSocket connection state:', state);
      }
    },
    unsubscribeFromChannel() {
      if (this.$echo) {
        console.log(`👋 Unsubscribing from channel: ticket.${this.ticketId}`);
        this.$echo.leave(`ticket.${this.ticketId}`);
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      }
    },
    goBack() {
      this.$router.push('/tickets');
    },
    getUserName(user) {
      if (!user) return 'User';
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      }
      if (user.first_name) return user.first_name;
      if (user.email) return user.email;
      return 'User';
    },
    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        in_progress: 'blue',
        resolved: 'green'
      };
      return colors[status] || 'grey';
    },
    formatStatus(status) {
      if (!status) return '';
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    formatTime(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        const hours = d.getHours();
        const minutes = d.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 || 12;
        const minuteStr = minutes < 10 ? '0' + minutes : minutes;
        return `${hour12}:${minuteStr} ${ampm}`;
      } catch (e) {
        return '';
      }
    },
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.messages-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
}

.message-received {
  background-color: #f5f5f5;
  border-bottom-left-radius: 4px;
}

.message-sent {
  background-color: #e3f2fd;
  border-bottom-right-radius: 4px;
  margin-left: auto;
}

.message-header {
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

.message-text {
  font-size: 0.95rem;
  margin: 4px 0;
}

.message-time {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 4px;
  text-align: right;
}

.my-message .message-time {
  text-align: right;
}
</style>
